import axios from 'axios';
import Cookies from 'universal-cookie';

import { parameters, getLocale } from '../../localization/strings';
import { Order } from '../../entities/order';
import { FormCheckout } from '../../entities/forms/checkout';
import { ShortAnswerProduct } from '../../entities/short-answer';

export interface PaymentMethod {
  identifier: string;
  method: string;
  title: string;
  image?: string;
  imageKey?: string;
}

export const initiatePayment = async (
  data: Partial<FormCheckout>,
  answerHash?: string,
  token?: string,
  patientId?: string,
  treatmentAgreement?: boolean,
  region?: 'eu' | 'uk' | 'us' | null,
): Promise<{ url: string }> => {
  const cookies = new Cookies();
  const url = `${parameters().api.baseUrl}/payments/initiate`;

  const payload: any = {
    ...data,
    answerHash,
    patientId,
    treatmentAgreement,
    language: getLocale(),
    cookies: cookies.getAll(),
  };

  if (region) {
    payload.region = region;
  }

  const result = await axios.post(url, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return result.data;
};

export const repeatPayment = async (data: Partial<FormCheckout>, hash: string): Promise<{ url: string }> => {
  const cookies = new Cookies();
  const url = `${parameters().api.baseUrl}/payments/repeat`;

  const result = await axios.post(url, {
    ...data,
    hash,
    cookies: cookies.getAll(),
  });

  return result.data;
};

export const validatePayment = async (
  hash: string,
): Promise<{ order: Order; product: ShortAnswerProduct; isRegistered: boolean }> => {
  const url = `${parameters().api.baseUrl}/payments/validate/${hash}`;

  const result = await axios.get(url);

  return result.data;
};

const isApplePaySupported = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return !!(window as any).ApplePaySession?.canMakePayments?.();
  } catch {
    return false;
  }
};

export const getPaymentMethods = async (language?: string, countryId?: number): Promise<PaymentMethod[]> => {
  const url = `${parameters().api.baseUrl}/payments/methods`;
  const result = await axios.get(url, {
    params: {
      language: language || getLocale(),
      countryId,
    },
  });

  const methods: PaymentMethod[] = result.data;

  return methods.filter((method) => !method.method.includes('applepay') || isApplePaySupported());
};

export const markOrderAsTracked = async (orderHash: string): Promise<{ success: boolean; message: string }> => {
  const url = `${parameters().api.baseUrl}/orders/mark-analytics-tracked/${orderHash}`;

  const result = await axios.post(url);

  return result.data;
};
